'use client';

/**
 * Token Refresh Manager
 *
 * Provides centralized token refresh logic with:
 * - Concurrency control (single refresh at a time)
 * - Request queueing during refresh
 * - Network vs auth error classification
 * - Exponential backoff retry for transient failures
 * - Proactive refresh before token expiry
 */

// Configuration
const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry
const MAX_RETRY_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 1000;

// Token status enum
export const TOKEN_STATUS = {
    VALID: 'valid',
    REFRESHING: 'refreshing',
    EXPIRING_SOON: 'expiring_soon',
    EXPIRED: 'expired',
    ERROR: 'error'
};

// Error types for classification
export const AUTH_ERROR_TYPES = {
    NETWORK: 'network',
    AUTH: 'auth',
    SERVER: 'server',
    UNKNOWN: 'unknown'
};

/**
 * Classifies an error as network, auth, or server error
 * @param {Error} error - The error to classify
 * @returns {string} - The error type
 */
export function classifyError(error) {
    if (!error) return AUTH_ERROR_TYPES.UNKNOWN;

    // Network errors
    if (
        error.name === 'TypeError' && error.message === 'Failed to fetch' ||
        error.name === 'AbortError' ||
        error.message?.includes('network') ||
        error.message?.includes('Network') ||
        error.message?.includes('ECONNREFUSED') ||
        error.message?.includes('ETIMEDOUT') ||
        error.message?.includes('ENOTFOUND') ||
        !navigator.onLine
    ) {
        return AUTH_ERROR_TYPES.NETWORK;
    }

    // Auth errors (should trigger logout)
    if (
        error.status === 401 ||
        error.status === 403 ||
        error.responseCode === 0 ||
        error.message?.includes('unauthorized') ||
        error.message?.includes('Unauthorized') ||
        error.message?.includes('invalid_token') ||
        error.message?.includes('token_expired') ||
        error.message?.includes('refresh_token_expired')
    ) {
        return AUTH_ERROR_TYPES.AUTH;
    }

    // Server errors (transient, may retry)
    if (
        error.status >= 500 ||
        error.message?.includes('Internal Server Error') ||
        error.message?.includes('Service Unavailable')
    ) {
        return AUTH_ERROR_TYPES.SERVER;
    }

    return AUTH_ERROR_TYPES.UNKNOWN;
}

/**
 * Check if error is a network error (should retry)
 * @param {Error} error
 * @returns {boolean}
 */
export function isNetworkError(error) {
    return classifyError(error) === AUTH_ERROR_TYPES.NETWORK;
}

/**
 * Check if error is an auth error (should logout)
 * @param {Error} error
 * @returns {boolean}
 */
export function isAuthError(error) {
    return classifyError(error) === AUTH_ERROR_TYPES.AUTH;
}

/**
 * Check if error is a server error (may retry)
 * @param {Error} error
 * @returns {boolean}
 */
export function isServerError(error) {
    return classifyError(error) === AUTH_ERROR_TYPES.SERVER;
}

/**
 * Check if error is retryable
 * @param {Error} error
 * @returns {boolean}
 */
export function isRetryableError(error) {
    const errorType = classifyError(error);
    return errorType === AUTH_ERROR_TYPES.NETWORK || errorType === AUTH_ERROR_TYPES.SERVER;
}

/**
 * Delay utility with exponential backoff
 * @param {number} attemptNumber - Current retry attempt (0-based)
 * @returns {Promise<void>}
 */
export function delay(attemptNumber) {
    const delayMs = Math.min(
        BASE_RETRY_DELAY_MS * Math.pow(2, attemptNumber),
        30000 // Max 30 seconds
    );
    // Add jitter (0-25% of delay)
    const jitter = delayMs * Math.random() * 0.25;
    return new Promise(resolve => setTimeout(resolve, delayMs + jitter));
}

/**
 * Calculate time until token expiry
 * @param {Date|string|number} expiresAt - Token expiry time
 * @returns {number} - Milliseconds until expiry (negative if expired)
 */
export function getTimeUntilExpiry(expiresAt) {
    if (!expiresAt) return -1;

    const expiryTime = expiresAt instanceof Date
        ? expiresAt.getTime()
        : new Date(expiresAt).getTime();

    return expiryTime - Date.now();
}

/**
 * Determine current token status based on expiry time
 * @param {Date|string|number} expiresAt - Token expiry time
 * @param {boolean} isRefreshing - Whether a refresh is in progress
 * @returns {string} - Token status
 */
export function getTokenStatus(expiresAt, isRefreshing = false) {
    if (isRefreshing) return TOKEN_STATUS.REFRESHING;

    const timeUntilExpiry = getTimeUntilExpiry(expiresAt);

    if (timeUntilExpiry < 0) return TOKEN_STATUS.EXPIRED;
    if (timeUntilExpiry < REFRESH_BUFFER_MS) return TOKEN_STATUS.EXPIRING_SOON;
    return TOKEN_STATUS.VALID;
}

/**
 * Check if token needs refresh (proactive refresh)
 * @param {Date|string|number} expiresAt - Token expiry time
 * @returns {boolean}
 */
export function needsRefresh(expiresAt) {
    const timeUntilExpiry = getTimeUntilExpiry(expiresAt);
    return timeUntilExpiry < REFRESH_BUFFER_MS;
}

/**
 * Token Refresh Manager class
 * Handles concurrent refresh requests and request queueing
 */
export class TokenRefreshManager {
    constructor() {
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.pendingRequests = [];
        this.tokenStatus = TOKEN_STATUS.VALID;
        this.statusListeners = new Set();
    }

    /**
     * Subscribe to token status changes
     * @param {Function} listener - Callback function
     * @returns {Function} - Unsubscribe function
     */
    subscribeToStatus(listener) {
        this.statusListeners.add(listener);
        return () => this.statusListeners.delete(listener);
    }

    /**
     * Update and broadcast token status
     * @param {string} status
     */
    setStatus(status) {
        this.tokenStatus = status;
        this.statusListeners.forEach(listener => {
            try {
                listener(status);
            } catch (e) {
                console.error('Token status listener error:', e);
            }
        });
    }

    /**
     * Execute a refresh operation with concurrency control
     * @param {Function} refreshFn - The refresh function to call
     * @returns {Promise<any>} - The refresh result
     */
    async executeRefresh(refreshFn) {
        // If already refreshing, return the existing promise
        if (this.isRefreshing && this.refreshPromise) {
            return this.refreshPromise;
        }

        this.isRefreshing = true;
        this.setStatus(TOKEN_STATUS.REFRESHING);

        this.refreshPromise = this._performRefreshWithRetry(refreshFn)
            .finally(() => {
                this.isRefreshing = false;
                this.refreshPromise = null;
            });

        return this.refreshPromise;
    }

    /**
     * Internal: Perform refresh with exponential backoff retry
     * @param {Function} refreshFn
     * @param {number} retryCount
     * @returns {Promise<any>}
     */
    async _performRefreshWithRetry(refreshFn, retryCount = 0) {
        try {
            const result = await refreshFn();
            this.setStatus(TOKEN_STATUS.VALID);
            this._processPendingRequests(result);
            return result;
        } catch (error) {
            console.error(`Token refresh attempt ${retryCount + 1} failed:`, error);

            // Check if we should retry
            if (isRetryableError(error) && retryCount < MAX_RETRY_ATTEMPTS) {
                console.log(`Retrying token refresh (attempt ${retryCount + 2}/${MAX_RETRY_ATTEMPTS + 1})...`);
                await delay(retryCount);
                return this._performRefreshWithRetry(refreshFn, retryCount + 1);
            }

            // If it's an auth error, we need to logout (handled by caller)
            if (isAuthError(error)) {
                this.setStatus(TOKEN_STATUS.ERROR);
                this._rejectPendingRequests(error);
                throw error;
            }

            // For other errors after max retries, reject pending requests
            this.setStatus(TOKEN_STATUS.ERROR);
            this._rejectPendingRequests(error);
            throw error;
        }
    }

    /**
     * Queue a request to be executed after refresh completes
     * @param {Function} requestFn - The request function
     * @returns {Promise<any>}
     */
    queueRequest(requestFn) {
        return new Promise((resolve, reject) => {
            this.pendingRequests.push({ requestFn, resolve, reject });
        });
    }

    /**
     * Process all pending requests after successful refresh
     * @param {any} refreshResult
     */
    _processPendingRequests(refreshResult) {
        const requests = [...this.pendingRequests];
        this.pendingRequests = [];

        requests.forEach(({ requestFn, resolve, reject }) => {
            try {
                resolve(requestFn(refreshResult));
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Reject all pending requests on refresh failure
     * @param {Error} error
     */
    _rejectPendingRequests(error) {
        const requests = [...this.pendingRequests];
        this.pendingRequests = [];

        requests.forEach(({ reject }) => {
            reject(error);
        });
    }

    /**
     * Get current refresh state
     * @returns {object}
     */
    getState() {
        return {
            isRefreshing: this.isRefreshing,
            tokenStatus: this.tokenStatus,
            pendingRequestCount: this.pendingRequests.length
        };
    }

    /**
     * Reset the manager state
     */
    reset() {
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.pendingRequests = [];
        this.setStatus(TOKEN_STATUS.VALID);
    }
}

// Singleton instance for app-wide use
export const tokenRefreshManager = new TokenRefreshManager();

export default tokenRefreshManager;
